import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';

/*
  Page numbers are windowed around the current page rather than always showing
  1–7. On a 40-page audit log the old version showed pages 1 to 7 forever, so
  from page 12 there was no way forward except Next, one page at a time.
*/
function pageWindow(page: number, totalPages: number, span = 5): number[] {
  const half = Math.floor(span / 2);
  let start = Math.max(1, page - half);
  const end = Math.min(totalPages, start + span - 1);
  start = Math.max(1, end - span + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function Pagination({
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  className,
}: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  className?: string;
}) {
  if (total === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const pages = pageWindow(page, totalPages);

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <p className="text-[11px] text-muted-foreground">
        <span className="font-semibold tabular-nums text-foreground">
          {from}–{to}
        </span>{' '}
        of <span className="font-semibold tabular-nums text-foreground">{total}</span>
      </p>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          icon={<ChevronLeft size={13} />}
        />
        {pages[0] > 1 && <span className="px-1 text-[11px] text-muted-foreground">…</span>}
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            aria-current={p === page ? 'page' : undefined}
            className={cn(
              'h-7 min-w-7 rounded-md px-1.5 text-[11px] font-semibold tabular-nums transition-colors',
              p === page
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {p}
          </button>
        ))}
        {pages[pages.length - 1] < totalPages && (
          <span className="px-1 text-[11px] text-muted-foreground">…</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label="Next page"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          icon={<ChevronRight size={13} />}
        />
      </div>
    </div>
  );
}
